import { CampaignPlanRequestDTO, ContentInventoryDTO, ContentKind, SocialCampaignDTO, VisualKeyDTO } from 'models/socialCampaign'
import { CheckCircleOutlineOutlined, WarningAmberOutlined } from '@mui/icons-material'
import { FC, useId, useMemo } from 'react'
import { GRAY_800, GRAY_900, GREEN_700, YELLOW_700 } from 'constants/styling/theme'
import {
  PLAN_POSTS_CHOICES,
  PLAN_WEEKS_CHOICES,
  cadenceDays,
  capacityLine,
  contentKindCards,
  formatPlanPace,
  joinAlternatives,
  planPace,
  withChoice
} from 'utils/socialCampaign/planAndContent'
import { Trans, useTranslation } from 'react-i18next'

import { ContentKindCard } from './ContentKindCard.component'
import { ContentKindProgress } from './contentKinds'
import { MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { SectionHead } from 'components/common/SectionHead'
import Typography from '@mui/material/Typography'
import styles from './CampaignPlanAndContent.module.sass'

interface Props {
  campaign: SocialCampaignDTO
  /** The property's content per kind: the counts on the cards; undefined while it is read */
  inventory: ContentInventoryDTO | undefined
  /** The plan sentence as chosen (saved or on its way) */
  plan: CampaignPlanRequestDTO
  /** Whether the plan draws on a kind, as chosen */
  isIncluded: (kind: ContentKind) => boolean
  /** The visuals left out as chosen (saved, or a choice on its way, ISSUE-3651); the campaign's when omitted */
  excludedVisuals?: VisualKeyDTO[]
  /**
   * A change is waiting or on its way: the drafts are being re-planned. Marked as `data-saving`, not `aria-busy`,
   * which would hold back the capacity line's announcement until the save answers.
   */
  saving: boolean
  /**
   * The drafts are being approved or launched: the plan and the kinds cannot change, or a write re-planning them
   * would race the approval (a 409, or drafts launched that the user never saw)
   */
  disabled?: boolean
  onPlanChange: (plan: Partial<CampaignPlanRequestDTO>) => void
  onIncludedChange: (kind: ContentKind, included: boolean) => void
  /** The purchase flow for this property; null when the user cannot order */
  orderHref: string | null
  /** Order chosen, once its new tab opened */
  onOrder?: (kind: ContentKind) => void
  onCreateWithAi: (kind: ContentKind) => void
  onUpload: (kind: ContentKind) => void
  /** Opens a kind's visuals to keep or leave out each one (ISSUE-3651) */
  onOpenVisuals?: (kind: ContentKind) => void
  /** Missing kinds being created with AI or ordered */
  progress?: Partial<Record<ContentKind, ContentKindProgress>>
}

/**
 * Section 1 of a draft campaign, "Plan & content" (ISSUE-3622, prototype v3 `contentSection`): the plan sentence
 * "[7 posts] across [3 weeks]" with the cadence strip and the pace; the capacity line, enough (green check) or a
 * warning naming the missing kinds that would add a post, those already ordered left out; the kinds of content the
 * property has, photos always in and the others included or left out by their checkbox; the kinds it does not have
 * yet, each naming the post it would add, with its ways in behind "+ Add". Adding content never adds posts: the plan
 * sentence sets their number.
 *
 * The backend computes the capacity, the posts using each kind and the posts a missing kind would add (ISSUE-3984);
 * the cadence strip shows the posts as the backend planned them. The brochure row of the prototype is hidden for now
 * (v3 decision 5).
 */
export const CampaignPlanAndContent: FC<Props> = ({
  campaign,
  inventory,
  plan,
  isIncluded,
  excludedVisuals,
  saving,
  disabled = false,
  onPlanChange,
  onIncludedChange,
  orderHref,
  onOrder,
  onCreateWithAi,
  onUpload,
  onOpenVisuals,
  progress,
}) => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const ids = `campaign-plan-${useId().replace(/:/g, '')}`
  const { capacity } = campaign

  const pace = formatPlanPace(planPace(plan.posts, plan.weeks), i18n.resolvedLanguage || i18n.language)
  const days = useMemo(() => cadenceDays(campaign.plan, campaign.posts, campaign.timezone), [campaign.plan, campaign.posts, campaign.timezone])
  const line = capacity ? capacityLine(capacity, plan.posts) : null
  // a kind already ordered is on its way: the warning does not ask for it again (prototype v3 `gains`)
  const toAdd = line?.tone === 'warn' ? line.missing.filter((kind) => progress?.[kind]?.state !== 'ordered') : []
  const cards = capacity ? contentKindCards(capacity, inventory, excludedVisuals ?? campaign.excludedVisuals, isIncluded) : []
  const ready = cards.filter((card) => card.available)
  const missing = cards.filter((card) => !card.available)

  // "a video, virtual staging, a floor plan or drone footage" (prototype `list`)
  const kindNouns = (kinds: ContentKind[]) =>
    joinAlternatives(kinds.map((kind) => t(`plan.capacity.noun.${kind}`)), i18n.resolvedLanguage || i18n.language)

  const renderCard = (card: (typeof cards)[number]) => (
    <ContentKindCard
      key={card.kind}
      card={card}
      progress={progress?.[card.kind]}
      orderHref={orderHref}
      onOrder={onOrder ? () => onOrder(card.kind) : undefined}
      onIncludedChange={(included) => onIncludedChange(card.kind, included)}
      onCreateWithAi={() => onCreateWithAi(card.kind)}
      onUpload={() => onUpload(card.kind)}
      onOpenVisuals={onOpenVisuals ? () => onOpenVisuals(card.kind) : undefined}
      disabled={disabled}
    />
  )

  // a saved value outside the choices (an older plan, a backend default) is shown as it is: a native select would
  // otherwise show its first option, and picking that one would fire no change
  const select = (id: string, label: string, value: number, choices: readonly number[], option: (count: number) => string, onChange: (count: number) => void) => (
    <>
      <label htmlFor={id} className={styles.srOnly}>{label}</label>
      <MUIDropdownInput id={id} isNativeSelect isChevronDecorative value={String(value)} disabled={disabled} onChange={(event) => onChange(Number(event.target.value))}>
        {withChoice(choices, value).map((count) => <option key={count} value={String(count)}>{option(count)}</option>)}
      </MUIDropdownInput>
    </>
  )

  return (
    <section className={styles.card} aria-labelledby={`${ids}-title`} data-saving={saving || undefined}>

      <SectionHead
        id={`${ids}-title`}
        titleComponent="h3"
        isDivided
        title={<span className={styles.headTitle}><span className={styles.num} aria-hidden="true">1</span>{t('plan.title')}</span>}
      />

      <div className={styles.plan}>

        <div className={styles.planRow}>

          {select(`${ids}-posts`, t('plan.posts_label'), plan.posts, PLAN_POSTS_CHOICES, (count) => t('plan.posts', { count }), (posts) => onPlanChange({ posts }))}

          <Typography variant="text-sm" component="span" color={GRAY_900}>{t('plan.across')}</Typography>

          {select(`${ids}-weeks`, t('plan.weeks_label'), plan.weeks, PLAN_WEEKS_CHOICES, (count) => t('plan.weeks', { count }), (weeks) => onPlanChange({ weeks }))}

          {/* the cadence: one mark per post on the plan's days */}
          <span className={styles.strip} aria-hidden="true" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
            {days.map((on, index) => <span key={index} className={styles.day} data-on={on} />)}
          </span>

          {/* GRAY_800, not the prototype's lighter grey: 4.4:1 on the beige plan, under AA for 12px text */}
          <Typography variant="text-xs" component="span" color={GRAY_800}>{t('plan.pace', { pace })}</Typography>

        </div>

        {line &&
          <Typography variant="text-sm" component="p" color={GRAY_800} className={styles.capacity} data-tone={line.tone} aria-live="polite">
            <span className={styles.capacityIcon}>
              {line.tone === 'ok'
                ? <CheckCircleOutlineOutlined sx={{ fontSize: '1.6rem', color: GREEN_700 }} />
                : <WarningAmberOutlined sx={{ fontSize: '1.6rem', color: YELLOW_700 }} />
              }
            </span>
            <span>
              {line.tone === 'ok'
                ? <Trans t={t} i18nKey="plan.capacity.enough" count={line.differentPosts} values={{ posts: line.posts }} components={{ strong: <strong /> }} />
                : <>
                  <Trans t={t} i18nKey="plan.capacity.covers" count={line.differentPosts} values={{ next: line.differentPosts + 1 }} components={{ strong: <strong /> }} />
                  {toAdd.length > 0 && <> {t('plan.capacity.add', { kinds: kindNouns(toAdd) })}</>}
                </>
              }
            </span>
          </Typography>
        }

      </div>

      {ready.length > 0 &&
        <div className={styles.field}>

          <Typography id={`${ids}-ready`} variant="text-sm" fontWeight={450} color={GRAY_800}>{t('plan.in_gallery')}</Typography>

          <div className={styles.kinds} role="group" aria-labelledby={`${ids}-ready`}>{ready.map(renderCard)}</div>

        </div>
      }

      {missing.length > 0 &&
        <div className={styles.field}>

          <Typography id={`${ids}-missing`} variant="text-sm" fontWeight={450} color={GRAY_800}>{t('plan.not_in_gallery')}</Typography>

          <div className={styles.kinds} role="group" aria-labelledby={`${ids}-missing`}>{missing.map(renderCard)}</div>

        </div>
      }

    </section>
  )
}
